import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getBranding, getTheme, getPublishedPages } from "../api/publicSite";
import { isDevHostname } from "../config/platform";

const PublicSiteContext = createContext(null);
const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || "partyrentalsuite.com").toLowerCase();

/**
 * Who owns tenant resolution for this page load?
 *   "path"    — /s/<subdomain>/…  → SiteRouter resolves from the URL and calls configure()
 *   "host"    — tenant subdomain or custom domain → SubdomainRouter resolves from the hostname
 *   "preview" — platform/dev host with no site prefix → localStorage or /preview-setup (dev convenience only)
 * In "path"/"host" mode we never guess a tenant: guessing is how one operator's catalog leaked onto another's site.
 */
export function resolutionMode() {
  if (/^\/s\/[^/]+/.test(window.location.pathname)) return "path";
  const host = window.location.hostname.toLowerCase();
  const isDevHost = isDevHostname(host);
  const isPlatformRoot = host === PLATFORM_DOMAIN || host === `www.${PLATFORM_DOMAIN}` || host === `app.${PLATFORM_DOMAIN}`;
  if (!isDevHost && !isPlatformRoot) return "host";
  return "preview";
}

export function PublicSiteProvider({ children }) {
  const [branding, setBranding] = useState(null);
  const [editorBranding, setEditorBranding] = useState(null);
  const [theme, setTheme] = useState(null);
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [micrositeId, setMicrositeId] = useState(null);
  const [tenantStatus, setTenantStatus] = useState(null); // { gate, message, banner, status }
  // Set once a router has resolved the tenant from the URL/host — a late preview-setup response must never override it.
  const authoritativeRef = useRef(false);

  // Listen for live branding updates from the Theme Editor (postMessage bridge)
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "theme-editor-branding" && e.data.branding) {
        setEditorBranding(e.data.branding);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Merge API branding with live editor overrides
  const mergedBranding = React.useMemo(() => {
    if (!editorBranding) return branding;
    if (!branding) return editorBranding;
    return {
      ...branding,
      ...editorBranding,
      // Deep-merge nested objects
      hero_config: { ...(branding.hero_config || {}), ...(editorBranding.hero_config || {}) },
      featured_config: { ...(branding.featured_config || {}), ...(editorBranding.featured_config || {}) },
    };
  }, [branding, editorBranding]);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const tid = localStorage.getItem("public_tenant_id");
      const mid = localStorage.getItem("public_microsite_id");
      const headers = {};
      if (tid) headers["X-Tenant-ID"] = tid;
      if (mid) headers["X-Microsite-ID"] = mid;

      const [brandRes, themeRes, navRes, statusRes] = await Promise.all([
        getBranding().catch(() => ({ data: {} })),
        getTheme().catch(() => ({ data: {} })),
        getPublishedPages().catch(() => ({ data: [] })),
        fetch(`${backendUrl}/api/public/site/tenant-status`, { headers }).then(r => r.json()).catch(() => null),
      ]);
      setBranding(brandRes.data);
      setTheme(themeRes.data);
      setNavigation(navRes.data || []);
      if (statusRes) setTenantStatus(statusRes);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (resolutionMode() !== "preview") return; // SiteRouter / SubdomainRouter will call configure()
    // Admin/driver surfaces only need the ids (SEO pages); skip the 4 public-site fetches there.
    const onTenantSurface = /^\/(admin|driver|platform|onboarding)(\/|$)/.test(window.location.pathname);
    const hydrate = onTenantSurface ? () => setLoading(false) : loadContext;
    const tid = localStorage.getItem("public_tenant_id");
    const mid = localStorage.getItem("public_microsite_id");
    if (tid && mid) {
      setTenantId(tid);
      setMicrositeId(mid);
      setConfigured(true);
      hydrate();
    } else {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      fetch(`${backendUrl}/api/public/site/preview-setup`)
        .then(r => r.json())
        .then(data => {
          if (authoritativeRef.current) return;
          if (data.tenant_id && data.microsite_id) {
            localStorage.setItem("public_tenant_id", data.tenant_id);
            localStorage.setItem("public_microsite_id", data.microsite_id);
            setTenantId(data.tenant_id);
            setMicrositeId(data.microsite_id);
            setConfigured(true);
            hydrate();
          } else {
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
    }
  }, [loadContext]);

  const configure = useCallback((tid, mid) => {
    authoritativeRef.current = true;
    localStorage.setItem("public_tenant_id", tid);
    localStorage.setItem("public_microsite_id", mid);
    setTenantId(prev => (prev === tid ? prev : tid));
    setMicrositeId(prev => (prev === mid ? prev : mid));
    setConfigured(true);
    loadContext();
  }, [loadContext]);

  const disconnect = () => {
    authoritativeRef.current = false;
    localStorage.removeItem("public_tenant_id");
    localStorage.removeItem("public_microsite_id");
    setTenantId(null);
    setMicrositeId(null);
    setConfigured(false);
    setBranding(null);
    setTheme(null);
    setNavigation([]);
    setTenantStatus(null);
  };

  return (
    <PublicSiteContext.Provider value={{ branding: mergedBranding, theme, navigation, loading, configured, configure, disconnect, tenantId, micrositeId, tenantStatus }}>
      {children}
    </PublicSiteContext.Provider>
  );
}

export function usePublicSite() {
  const ctx = useContext(PublicSiteContext);
  if (!ctx) throw new Error("usePublicSite must be inside PublicSiteProvider");
  return ctx;
}
